/**
 * Release Run Pane
 *
 * @module features/releases/components/ReleaseRunPane
 * @description The timeline's master-detail detail pane for a selected
 * release run — replaces the old standalone `ReleaseRunDetail` page. The
 * selected band already carries the run's identity and runs physically into
 * this pane, so the pane repeats none of it: a slim header
 * (`ReleaseRunPaneHeader`: git range, duration, Close), then the regression
 * results (the same `RunTriageLockup` view the test-execution page uses) stacked
 * above the release notes. No tabs and no agent transcript: talking to the
 * release agent — including answering its open questions — happens in the
 * assistant, which auto-opens on a live agent (`useReleaseAgentAutoOpen`).
 * Cancelling the run is the pane's one destructive action and sits at the
 * bottom in a `DangerSection`, per the detail-page convention.
 *
 * This component is purely the pane's *content* — the host slot's width
 * lives in `ReleasePropertyTimeline`. The reveal animation (`PaneReveal`)
 * lives in `ReleaseRunPaneReveal` and wraps this pane's body internally, so
 * opening/closing it never changes the slot's size.
 */

import React, { useState } from 'react';
import { CircleNotch, XCircle } from '@phosphor-icons/react';
import { Button, DangerAction, DangerSection, EmptyState, Text } from '@chatsdet/ui';
import { useReleaseRun } from '../useReleases';
import { getReleaseRunStatusMeta, isActiveStatus } from '../releaseTypes';
import { ReleaseRunPaneHeader } from './ReleaseRunPaneHeader';
import { ReleaseRegressionsList } from './ReleaseRegressionsList';
import { ReleaseTestResultsTab } from './ReleaseTestResultsTab';
import { ReleaseReport } from './ReleaseReport';
import { CancelRunDialog } from './CancelRunDialog';
import { PaneReveal, type BandSlice } from './ReleaseRunPaneReveal';

export function ReleaseRunPane({
  runId,
  onClose,
  bandSlice,
}: {
  runId: string;
  onClose: () => void;
  /** The selected band's column-relative slice the pane reveals from — see `ReleaseRunPaneReveal`. */
  bandSlice: BandSlice;
}) {
  const { data, isLoading, error } = useReleaseRun(runId);
  const run = data?.run;
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const hasRegressions = (run?.regressionTestsTotal ?? 0) > 0;
  const hasWorkflowExecution = Boolean(run?.workflowTestExecutionId);
  const hasTestResults = hasWorkflowExecution || hasRegressions;
  const hasNotes =
    run?.status === 'completed' ||
    run?.status === 'completed_with_errors' ||
    Boolean(run?.releaseNotesMarkdown);

  let body: React.ReactNode;
  if (error) {
    body = (
      <div className="p-6 text-center">
        <Text className="text-destructive">Failed to load run details.</Text>
      </div>
    );
  } else if (isLoading || !run) {
    body = (
      <div className="flex flex-1 items-center justify-center py-24">
        <CircleNotch className="animate-spin text-neutral-400" size={32} />
      </div>
    );
  } else {
    body = (
      <>
        <ReleaseRunPaneHeader run={run} onClose={onClose} />

        <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4">
          {hasTestResults &&
            (hasWorkflowExecution ? (
              <ReleaseTestResultsTab
                executionId={run.workflowTestExecutionId!}
                regressionConfig={run.regressionConfig}
              />
            ) : (
              <ReleaseRegressionsList releaseRunId={runId} />
            ))}
          {hasNotes ? (
            <ReleaseReport
              releaseRunId={runId}
              runStatus={run.status}
              releaseNotesMarkdown={run.releaseNotesMarkdown ?? null}
              releaseNotesCompletedAt={run.releaseNotesCompletedAt ?? null}
              commitsAnalyzed={run.commitsAnalyzed ?? 0}
            />
          ) : (
            // A run that produced neither results nor notes must still say
            // what happened to it — a run that failed before its suites ran
            // (e.g. no configured suites) would otherwise be indistinguishable
            // from one that hasn't started, and the band's own status line
            // isn't guaranteed to be on screen beside the pane.
            !hasTestResults && (
              <EmptyState
                size="sm"
                title={isActiveStatus(run.status) ? 'Nothing to show yet' : getReleaseRunStatusMeta(run.status).label}
                description={isActiveStatus(run.status) ? undefined : 'No regression results or release notes were produced.'}
              />
            )
          )}

          {isActiveStatus(run.status) && (
            <DangerSection className="mt-auto">
              <DangerAction
                title="Cancel run"
                description="Aborts every running job for this release run. It cannot be resumed."
              >
                <Button
                  variant="destructive"
                  size="sm"
                  onPress={() => setCancelModalOpen(true)}
                  iconLeft={<XCircle size={16} />}
                >
                  Cancel run
                </Button>
              </DangerAction>
            </DangerSection>
          )}
        </div>

        <CancelRunDialog runId={run.id} isOpen={cancelModalOpen} onOpenChange={setCancelModalOpen} />
      </>
    );
  }

  // Absolutely positioned over the host slot (which is `relative`) —
  // during a run switch, `AnimatePresence` briefly mounts the outgoing and
  // incoming pane together, and overlaying them exactly is what keeps that
  // transition from ever changing the column's own layout. No background of
  // its own: the white is `PaneReveal`'s clipped surface — see its module doc.
  return (
    <section aria-label="Release run details" className="absolute inset-0 flex flex-col">
      <PaneReveal bandSlice={bandSlice}>{body}</PaneReveal>
    </section>
  );
}
